import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { RespondentSelection } from "../types/respondentSelection";
/* eslint-disable import/export */
export abstract class DeleteRespondentRPC implements
  LeoRPC<DeleteRespondentRPC.Request, DeleteRespondentRPC.Response, DeleteRespondentRPC.Errors.Errors>
{
  abstract execute(
    request: DeleteRespondentRPC.Request
  ): Promise<LeoRPCResult<DeleteRespondentRPC.Response, DeleteRespondentRPC.Errors.Errors>>;
}

export namespace DeleteRespondentRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    respondentSelection: RespondentSelection.RespondentSelectionDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isDeleteRespondentRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly respondentSelection: RespondentSelection.RespondentSelection;
  
  
    constructor(
    projectId: LeoUUID, 
    respondentSelection: RespondentSelection.RespondentSelection
    ) {
      this.projectId = projectId;
      this.respondentSelection = respondentSelection;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const respondentSelection = RespondentSelection.fromDTO(getObject(dto, "respondentSelection", InvalidRequestRespondentSelectionError));
      return new Request(
        projectId,
        respondentSelection
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          respondentSelection: this.respondentSelection.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `DeleteRespondentRPC(projectId='${this.projectId}',respondentSelection='${this.respondentSelection}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `DeleteRespondentRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidRespondent = "INVALID_RESPONDENT",
    ProjectArchived = "PROJECT_ARCHIVED",
    DeletedColumnFilter = "DELETED_COLUMN_FILTER"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `DeleteRespondentRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidRespondentDTO {
      code: string;
    }
    
    export class InvalidRespondent extends LeoRPCError<RPCError> {
      readonly isInvalidRespondent: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidRespondent;
        super(code);
      }
    
    
      toDTO(): InvalidRespondentDTO {
        const dto: InvalidRespondentDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidRespondent.prototype.toString = function toString(): string {
      return `DeleteRespondentRPC.INVALID_RESPONDENT()`;
    };
    
    
    export interface ProjectArchivedDTO {
      code: string;
    }
    
    export class ProjectArchived extends LeoRPCError<RPCError> {
      readonly isProjectArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectArchived;
        super(code);
      }
    
    
      toDTO(): ProjectArchivedDTO {
        const dto: ProjectArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectArchived.prototype.toString = function toString(): string {
      return `DeleteRespondentRPC.PROJECT_ARCHIVED()`;
    };
    
    
    export interface DeletedColumnFilterDTO {
      code: string;
    }
    
    export class DeletedColumnFilter extends LeoRPCError<RPCError> {
      readonly isDeletedColumnFilter: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DeletedColumnFilter;
        super(code);
      }
    
    
      toDTO(): DeletedColumnFilterDTO {
        const dto: DeletedColumnFilterDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DeletedColumnFilter.prototype.toString = function toString(): string {
      return `DeleteRespondentRPC.DELETED_COLUMN_FILTER()`;
    };
    

    export type Errors = InvalidProjectId | InvalidRespondent | ProjectArchived | DeletedColumnFilter;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidRespondentDTO | ProjectArchivedDTO | DeletedColumnFilterDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidRespondent:
          return new InvalidRespondent();
        case RPCError.ProjectArchived:
          return new ProjectArchived();
        case RPCError.DeletedColumnFilter:
          return new DeletedColumnFilter();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends DeleteRespondentRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestRespondentSelectionError extends DeleteRespondentRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRespondentSelectionError";
  }
}


export class InvalidResponseInvalidProjectIdError extends DeleteRespondentRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidRespondentError extends DeleteRespondentRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidRespondentError";
  }
}

export class InvalidResponseProjectArchivedError extends DeleteRespondentRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectArchivedError";
  }
}

export class InvalidResponseDeletedColumnFilterError extends DeleteRespondentRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDeletedColumnFilterError";
  }
}

